import { Graphback, useStorageKeys } from '@gripp/shared-logic';
import { ColumnData, LoadingIndicator, SlideLayout } from '@gripp/shared-ui';
import { useLayoutEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { AssetRouteProvider } from '../../assets/assetRouteContext';
import { JoinSharedAssetView } from '../../assets/components/joinSharedAssetView';
import { usePathWithParams } from '../../shared/hooks/usePathWithParams';
import { useResolveShareLink } from '../hooks/useShareIssue';
import { Issue } from './issue';

export type ShareTarget = {
  subjectType: string;
  subjectId: string;
  sharedBy?: { id: string; name?: { first?: string; last?: string } } | null;
};

type ResolveShareLinkResponse = {
  share?: { resolveShareLink?: ShareTarget };
};

type ShareViewProps = {
  /**
   * The viewer is signed out (rendered by `AnonymousShareView`). Replies are
   * stamped as the sharer's guest, the asset link opens the QR-flow asset
   * page, and tapping an anonymous author starts sign-up with this URL as the
   * return path.
   */
  anonymous?: boolean;
};

// `/share/:token` — renders the issue view in place (URL stays on the link).
export const ShareView = ({ anonymous = false }: ShareViewProps) => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const storageKeys = useStorageKeys();
  const currentPath = usePathWithParams();

  // Set before descendant fetches so the first getIssue carries the header.
  useLayoutEffect(() => {
    Graphback.setShareToken(token ?? null);
    return () => Graphback.setShareToken(null);
  }, [token]);

  const { data, isLoading, isError } = useResolveShareLink(token) as {
    data?: ResolveShareLinkResponse;
    isLoading: boolean;
    isError: boolean;
  };
  const target = data?.share?.resolveShareLink;

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (isError || !token || !target) {
    return <Navigate to="/" replace />;
  }

  // An ASSET link is an invitation, not a view link: there is no anonymous
  // asset surface behind it, and opening it is the act of joining. Where that
  // lands — the app on a phone, the create-profile page while signed out, the
  // asset itself once signed in — is the dispatcher's call, and it renders
  // only what refuses.
  if (target.subjectType === 'ASSET') {
    return <JoinSharedAssetView token={token} signedOut={anonymous} />;
  }

  if (target.subjectType !== 'ISSUE') {
    return <Navigate to="/" replace />;
  }

  // The asset read never needed the share token, so the link is a plain route
  // change; browser back returns here and the token is re-set on mount.
  const onAssetPress = (assetId: string) =>
    navigate(anonymous ? `/anonymous/assets/${assetId}` : `/assets/${assetId}`);

  // Same two keys ProtectedRoute writes when it bounces a signed-out visitor:
  // `redirectPath` for the token handshake, `pendingDestination` because a
  // brand-new account is routed through /onboarding, which never reads the
  // first one. `/login` carries the register link.
  const onAnonymousAuthorPress = () => {
    localStorage.setItem(storageKeys.redirectPath, currentPath);
    localStorage.setItem(storageKeys.pendingDestination, currentPath);
    navigate('/login');
  };

  const column: ColumnData = {
    title: 'activities.detail.title',
    showHeader: false,
    disableScroll: true,
    mainComponent: (
      <AssetRouteProvider
        value={{
          basePath: `/activities/${target.subjectId}`,
          listPath: '/activities',
          isWorkspaceMode: false,
          issueId: target.subjectId,
        }}
      >
        <Issue
          issueId={target.subjectId}
          isComingFromActivities
          forceShowAssetLink
          readOnlyViewer={anonymous}
          anonymousSharedBy={
            anonymous ? target.sharedBy ?? undefined : undefined
          }
          onAnonymousAuthorPress={
            anonymous ? onAnonymousAuthorPress : undefined
          }
          onAssetPressOverride={onAssetPress}
        />
      </AssetRouteProvider>
    ),
  };

  return <SlideLayout leftColumn={column} />;
};
